/** What kind of attachment a file is, as far as the console can tell — the
 *  server's `kind` once uploaded, a guess from the browser's MIME type while
 *  the file is still only staged. */
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import DataObjectOutlinedIcon from '@mui/icons-material/DataObjectOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import type { SvgIconProps } from '@mui/material';

import type { DiagnoseAttachmentKind } from '@/api/diagnoseAttachments';

const STRUCTURED = /(xml|json|yaml|html)/i;

/** A best guess before the server has looked at the bytes. */
export function guessKind(mime: string, name: string): DiagnoseAttachmentKind {
  const lower = name.toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf' || lower.endsWith('.pdf')) return 'pdf';
  if (mime.startsWith('text/') || STRUCTURED.test(mime) || /\.(txt|log|xml|json|har|csv|md|ya?ml|eml)$/.test(lower)) {
    return 'text';
  }
  if (/\.(docx|rtf|xlsx|xls)$/.test(lower)) return 'document';
  return 'other';
}

export const KIND_LABEL: Record<DiagnoseAttachmentKind, string> = {
  image: 'Image',
  pdf: 'PDF',
  text: 'Text',
  document: 'Document',
  other: 'File',
};

/** Whether the model can read this kind's content (vs. only its name). */
export function modelCanRead(kind: DiagnoseAttachmentKind): boolean {
  return kind !== 'other';
}

export function KindIcon({
  kind,
  contentType = '',
  ...props
}: { kind: DiagnoseAttachmentKind; contentType?: string } & SvgIconProps) {
  switch (kind) {
    case 'image':
      return <ImageOutlinedIcon {...props} />;
    case 'pdf':
      return <PictureAsPdfOutlinedIcon {...props} />;
    case 'text':
      return STRUCTURED.test(contentType) ? (
        <DataObjectOutlinedIcon {...props} />
      ) : (
        <DescriptionOutlinedIcon {...props} />
      );
    case 'document':
      return <ArticleOutlinedIcon {...props} />;
    default:
      return <InsertDriveFileOutlinedIcon {...props} />;
  }
}
